import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class CrmAuthCookieService {
  private readonly accessTokenCookie = 'wm_access_token';
  private readonly refreshTokenCookie = 'wm_refresh_token';

  private readonly accessTokenExpiryKey = 'wm_access_token_expiry';
  private readonly refreshTokenExpiryKey = 'wm_refresh_token_expiry';

  setAuthCookies(payload: {
    accessToken: string;
    refreshToken: string;
    accessTokenExpiresUtc: string;
    refreshTokenExpiresUtc: string;
  }): void {
    // Both cookies are given the *refresh* token's lifetime on purpose. Tagging
    // the access-token cookie with its own (short) expiry makes the browser
    // delete it the moment the token goes stale, so a user returning to a
    // closed/backgrounded browser has no access token at all — hasAuthCookies()
    // reports "not signed in" and the route guards bounce them to /login even
    // though the refresh token is still perfectly valid. Actual expiry is
    // enforced by the server and by the stored expiry timestamps below.
    this.writeCookie(this.accessTokenCookie, payload.accessToken, payload.refreshTokenExpiresUtc);
    this.writeCookie(this.refreshTokenCookie, payload.refreshToken, payload.refreshTokenExpiresUtc);

    // Persist expiry times for the token-expiry/idle services to read.
    // localStorage, not sessionStorage: the tokens themselves live in cookies
    // that outlive the browser session, so expiry data kept in sessionStorage
    // vanishes on browser restart and leaves the expiry service permanently
    // blind (it bails out when the expiry is unknown and never refreshes).
    if (typeof window !== 'undefined') {
      localStorage.setItem(this.accessTokenExpiryKey, payload.accessTokenExpiresUtc);
      localStorage.setItem(this.refreshTokenExpiryKey, payload.refreshTokenExpiresUtc);
      sessionStorage.removeItem(this.accessTokenExpiryKey);
      sessionStorage.removeItem(this.refreshTokenExpiryKey);
    }
  }

  getAccessTokenExpiresAt(): number | null {
    const stored = this.readExpiry(this.accessTokenExpiryKey);
    if (stored !== null) return stored;

    // Fallback for sessions established before the storage move, and for any
    // state loss: read `exp` straight off the JWT.
    return this.readJwtExpiry(this.getAccessToken());
  }

  getRefreshTokenExpiresAt(): number | null {
    return this.readExpiry(this.refreshTokenExpiryKey);
  }

  getAccessToken(): string {
    return this.readCookie(this.accessTokenCookie);
  }

  getRefreshToken(): string {
    return this.readCookie(this.refreshTokenCookie);
  }

  hasAuthCookies(): boolean {
    return Boolean(this.getAccessToken() && this.getRefreshToken());
  }

  clearAuthCookies(): void {
    this.deleteCookie(this.accessTokenCookie);
    this.deleteCookie(this.refreshTokenCookie);
    if (typeof window !== 'undefined') {
      localStorage.removeItem(this.accessTokenExpiryKey);
      localStorage.removeItem(this.refreshTokenExpiryKey);
      sessionStorage.removeItem(this.accessTokenExpiryKey);
      sessionStorage.removeItem(this.refreshTokenExpiryKey);
    }
  }

  /** Reads an expiry timestamp, falling back to the pre-migration
   *  sessionStorage location so an already-signed-in tab keeps working. */
  private readExpiry(key: string): number | null {
    if (typeof window === 'undefined') return null;

    const raw = localStorage.getItem(key) ?? sessionStorage.getItem(key);
    if (!raw) return null;

    const ts = Date.parse(raw);
    return Number.isFinite(ts) ? ts : null;
  }

  private readJwtExpiry(token: string): number | null {
    if (!token) return null;

    const payload = token.split('.')[1];
    if (!payload) return null;

    try {
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = JSON.parse(atob(normalized)) as { exp?: number };
      return typeof decoded.exp === 'number' ? decoded.exp * 1000 : null;
    } catch {
      return null;
    }
  }

  private writeCookie(name: string, value: string, expiresUtc: string): void {
    if (typeof document === 'undefined') {
      return;
    }

    const expiresAt = Date.parse(expiresUtc);
    const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
    const expiresAttribute = Number.isFinite(expiresAt)
      ? `; Expires=${new Date(expiresAt).toUTCString()}`
      : '';

    // SameSite=Strict, not Lax. Nothing depends on these cookies being *sent* on
    // a cross-site navigation into the app — the API is authenticated by the
    // Bearer header the interceptor attaches, not by the cookie — and JS can
    // still read them via document.cookie on arrival regardless of SameSite. So
    // Strict costs nothing here and removes the CSRF vector these cookies would
    // otherwise carry into any endpoint that starts trusting them.
    document.cookie = `${name}=${encodeURIComponent(value)}${expiresAttribute}; Path=/; SameSite=Strict${secure}`;
  }

  private readCookie(name: string): string {
    if (typeof document === 'undefined') {
      return '';
    }

    const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
  }

  private deleteCookie(name: string): void {
    if (typeof document === 'undefined') {
      return;
    }

    // Name/domain/path is what identifies the cookie to overwrite, so SameSite
    // here is cosmetic — kept in step with writeCookie so the two read alike.
    document.cookie = `${name}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/; SameSite=Strict`;
  }
}
